import React, { RefObject, useCallback, useEffect, useMemo, useState } from 'react';
import PopOverNav from '../../components/navigation/PopOverNav';
import { SameLevelInpageNavigation } from '../../../types';
import { useInPageDispatch, useInPageNavContext } from '../../context';
import { compareUrlLastSegments, throttle } from '../../utils/utils';
import { INPAGENAV_ACTIONS } from '../../reducers/inPageNavReducer';
import useLinkElements from '../use-list-elements';

export interface IPriorityNavProps {
  refNavUpdated: RefObject<HTMLElement> | undefined;
  TopLevelNavRef: RefObject<HTMLElement> | undefined;
  activePriortyDropDown: boolean | undefined;
}

const howManyItemsInMenuArray = (array: number[], outerWidth: number) => {
  let total = 0;
  let items = 0;

  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < array.length; i++) {
    total += array[i];
    if (total > outerWidth) {
      items += 1;
    }
  }

  return items;
};

export const usePriorityNav = (
  props: IPriorityNavProps,
): {
  popOverNav: React.ReactElement | null;
} => {
  const [priorityItems, setPriorityItems] = useState<SameLevelInpageNavigation[]>([]);
  const { sameLevelInpageNavigation, priorityNavActive } = useInPageNavContext();
  // -1 means no overflow item is active; 0+ is a valid index into priorityItems.
  // Using -1 (not 0) avoids falsely marking the first overflow item as active on initial load.
  const [activeLink, setActiveLink] = useState<number>(-1);
  const [priorityItemslength, setPriorityItemslength] = useState<number>(0);
  const dispatch = useInPageDispatch();

  const linkElements = useLinkElements('.inpage-nav-menu .top-ul-list li a.top-level-link', [
    priorityNavActive,
  ]);

  const widthsArr = useMemo(() => {
    if (!props.TopLevelNavRef?.current || !priorityNavActive?.isDesktop) return [];
    const items = [...props.TopLevelNavRef.current.children];
    const itemsArr = items.map((item) => item.clientWidth).filter((item) => item > 0); // remove any hidden items
    itemsArr.pop(); // remove the last item which is the toggle dropdown menu icon

    // fix for initial load and resizing the navigation menu from large to small
    // and last the element item wraps and disappears
    if (priorityItemslength === 0) {
      const lastElement = itemsArr.at(-1) || 0;
      itemsArr.push(lastElement + 50);
    }
    return itemsArr;
  }, [props.TopLevelNavRef, sameLevelInpageNavigation, priorityNavActive]);

  const updateNavigation = useCallback(() => {
    // Get width of all items in navigation menu
    const outerWidth = props.refNavUpdated?.current?.offsetWidth || 0;
    const arrayAmount = howManyItemsInMenuArray(widthsArr, outerWidth || 0) || 0;
    const itemsLength = sameLevelInpageNavigation?.length || 0;
    const slicedAmount = itemsLength - arrayAmount;
    const prioItems = slicedAmount < 0 ? [] : sameLevelInpageNavigation?.slice(slicedAmount);
    // remove items from keyboard navigation if hidden in Desktop
    if (linkElements && priorityNavActive?.isDesktop) {
      linkElements.forEach((el, index) => {
        if (index >= slicedAmount) {
          el.setAttribute('tabindex', '-1');
        } else {
          el.removeAttribute('tabindex');
        }
      });
    }

    setPriorityItems(prioItems || []);
    if (dispatch) {
      dispatch({
        type: INPAGENAV_ACTIONS.PRIORITY_NAVIGATION,
        payload: {
          priorityNavigation: prioItems,
        },
      });
    }
    setPriorityItemslength(prioItems?.length || 0);
  }, [widthsArr]);

  // Compute activeLink based on whether the current page URL matches an overflow item
  useEffect(() => {
    if (priorityItems?.length > 0) {
      const currentPath = typeof window !== 'undefined' ? window?.location?.pathname : '';

      const directMatch = priorityItems.findIndex((item) => {
        const itemUrl = item.fields?.href?.path || item.fields?.linkUrl || '';
        return compareUrlLastSegments(currentPath, itemUrl);
      });

      if (directMatch >= 0) {
        setActiveLink(directMatch);
        return;
      }

      const parentMatch = priorityItems.findIndex((item) =>
        item.fields?.subLinks?.some((sub) => {
          const subUrl = sub.fields?.href?.path || sub.fields?.linkUrl || '';
          return compareUrlLastSegments(currentPath, subUrl);
        }),
      );

      setActiveLink(parentMatch >= 0 ? parentMatch : -1);
    } else {
      setActiveLink(-1);
    }
  }, [priorityItems]);

  useEffect(() => {
    const handleResize = throttle(() => {
      updateNavigation();
    }, 100);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [updateNavigation]);

  // onload trigger
  useEffect(() => {
    if (widthsArr.length > 0) updateNavigation();
  }, [widthsArr]);

  const popOverNav = useMemo(() => {
    return priorityItems.length > 0 ? (
      <PopOverNav
        visible={props.activePriortyDropDown || false}
        prioprityNavigation={priorityItems}
        activeLink={activeLink}
      />
    ) : null;
  }, [props.activePriortyDropDown, priorityItems, activeLink]);

  return {
    popOverNav,
    activeLink,
    priorityItemslength,
  } as {
    popOverNav: React.ReactElement | null;
    activeLink: number;
    priorityItemslength: number;
  };
};
export default usePriorityNav;
